/**
 * Director CIBIL API client.
 *
 *   GET  /cibil/director/:customer_id     -> getDirectorsCibil
 *   POST /cibil/director-fetch            -> fetchDirectorCibil
 *   GET  /cibil/view/:id/:director_uuid  -> getDirectorCibilView
 */
import type {
  VendorCibilReport,
  ViewCibilApiResponse,
} from "../lib/cibil/types";
import apiService from "./apiService";

/** Shape of each row returned by GET /cibil/director/:customer_id */
export interface DirectorCibilRow {
  /** Primary key of customer_directors_cibil; null when not fetched. */
  id: number | null;
  director_uuid: string;
  full_name: string;
  pan: string;
  mobile: string;
  email?: string | null;
  designation_name?: string | null;
  cibil_score: number | null;
  dpd_accounts: number | null;
  overdue: number | null;
  enquires: number | null;
  max_dpd: number | null;
  /** ISO timestamp when the CIBIL report was fetched / stored. */
  created_at?: string | null;
  /** "CIBIL report not fetched yet" when not fetched, else null */
  message: string | null;
  /** Apollo profile photo; null when unavailable. */
  apollo_photo_url?: string | null;
}

/** Body for POST /cibil/director-update-contact */
export interface UpdateDirectorCibilContactInput {
  customer_id: string;
  director_uuid: string;
  mobile?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  name?: string | null;
  pan?: string | null;
}

/** POST /cibil/director-update-contact — persist email/mobile on CIBIL director row */
// export async function updateDirectorCibilContact(
//   input: UpdateDirectorCibilContactInput
// ): Promise<void> {
//   await apiService.post(
//     `/cibil/director-update-contact`,
//     input as unknown as Record<string, unknown>
//   );
// }

/** Body for POST /cibil/director-fetch */
export interface FetchDirectorCibilInput {
  name: string;
  mobile: string;
  pan: string;
  consent_text: string;
  email?: string;
  customer_id?: string;
  director_uuid?: string;
}

function unwrapList(payload: unknown): DirectorCibilRow[] {
  if (Array.isArray(payload)) return payload as DirectorCibilRow[];
  if (payload && typeof payload === "object" && "data" in payload) {
    const data = (payload as { data: unknown }).data;
    if (Array.isArray(data)) return data as DirectorCibilRow[];
  }
  return [];
}

function unwrapRow(payload: unknown): DirectorCibilRow {
  if (payload && typeof payload === "object" && "data" in payload) {
    const data = (payload as { data: unknown }).data;
    if (data && typeof data === "object") return data as DirectorCibilRow;
  }
  return payload as DirectorCibilRow;
}

/** GET /cibil/director/:customer_id — list of directors for the customer */
export async function getDirectorsCibil(
  customerId: string
): Promise<DirectorCibilRow[]> {
  const res = await apiService.get<unknown>(`/cibil/director/${customerId}`);
  return unwrapList(res);
}

/** POST /cibil/director-fetch — trigger a fresh CIBIL pull for a director */
export async function fetchDirectorCibil(
  input: FetchDirectorCibilInput
): Promise<DirectorCibilRow> {
  const res = await apiService.post<unknown>(
    `/cibil/director-fetch`,
    input as unknown as Record<string, unknown>
  );
  return unwrapRow(res);
}

/**
 * GET /cibil/view/:id/:director_uuid — full vendor CIBIL JSON.
 * apiService already returns the HTTP body, so `response.data` is the report.
 */
export async function getDirectorCibilView(
  id: string | number,
  directorsUuid: string
): Promise<VendorCibilReport> {
  const response = (await apiService.get(
    `/cibil/view/${id}/${directorsUuid}`
  )) as ViewCibilApiResponse;
  return response.data;
}
